import { Challenge, challengeHud, challengePrompt, spotAt, type ChallengeHud, type ChallengePrompt, type ChallengeSpot } from "./challenge";
import type { Collectable } from "./collectables";
import { DEMO_STEP, DemoPlayer, type DemoLine } from "./demo";
import { hubAt, hubName, type HubSpot } from "./hubs";
import type { HudMessage, RunHud } from "./Game";
import { BUTTONS, type InputFrame } from "./input";
import { riding, Run, runBusy, type Goal, type MapGoals } from "./run";
import type { Skater, SkaterEvent } from "./skater";
import type { Cue } from "./sound/cues";

/**
 * What a session asks of `Game`: sounds, callouts, and the session-wide
 * bookkeeping (goals done this session, the HUD's goal list) that outlives
 * any one session.
 */
export interface SessionHost {
  play(cue: Cue): void;
  /** Sets the HUD callout. */
  say(kind: HudMessage["kind"], text: string): void;
  clearMessage(): void;
  /**
   * Marks a goal done this session; the stat points it paid (only the first
   * time, and only rungs that pay, #66). Null when it doesn't count because
   * a cheat was on (#119).
   */
  goalDone(goalId: string): number | null;
  /** Rebuilds the HUD's goal list (see `Game.refreshGoals`). */
  refreshGoals(): void;
}

/**
 * What `Game` is doing (#48): free skate, demo mode, a 2-minute run or a
 * free-skate challenge. `Game` holds exactly one and feeds it the same
 * hooks whichever it is, so which state wins lives here, not in the order
 * of `if`s in `Game.frame`.
 */
export type Session = FreeSkateSession | DemoSession | RunSession | ChallengeSession;

/** What a press does before it reaches the sim: stop the demo, start a marker's challenge, or open a hub's list (#86). */
export type SessionPress = { type: "stopDemo" } | { type: "challenge"; spot: ChallengeSpot } | { type: "hub"; hub: HubSpot };

/** The hooks every session answers. */
interface SessionBase {
  /** The sim holds still: a run's results screen, or a challenge's start or end card. */
  readonly holding: boolean;
  /** This session's goals for the Goals screen, or null to show the map's. */
  goals(): Goal[] | null;
  /** One sim step: picks up what the skater's touching. */
  touch(skater: Skater): void;
  /** After a frame's sim steps: `dt` of sim time and their events. */
  step(dt: number, skater: Skater, events: readonly SkaterEvent[]): void;
  /** A frame's presses, before the sim sees them. */
  onPress(input: InputFrame): SessionPress | null;
}

/**
 * A goal session: a 2-minute run or a free-skate challenge, with the
 * pickups, goals and the screens that stop the sim.
 */
export interface GoalSession extends SessionBase {
  readonly kind: "run" | "challenge";
  /** The run doing the clock, pickups and goal logic (a challenge's is a one-goal run). */
  readonly run: Run;
}

/**
 * Free skate proper: the map's challenge hubs (#86) are out, and △ in one
 * opens its list; on a map without hubs, each challenge has a marker and △
 * in one starts it. `Game` keeps one for the whole session and hands it each
 * map's hubs or markers.
 */
export class FreeSkateSession implements SessionBase {
  readonly kind = "free";
  readonly holding = false;
  /** The map's challenge markers (none on a map with hubs). */
  spots: readonly ChallengeSpot[] = [];
  /** The map's hubs (#86), in tier order. */
  hubs: readonly HubSpot[] = [];
  /**
   * The marker the skater's in, if one could start now. Found each sim step
   * in free skate; `Game` clears it on the first sim step of anything else.
   */
  near: ChallengeSpot | null = null;
  /** The hub the skater's in, likewise. */
  nearHub: HubSpot | null = null;

  /** What a locked marker or hub (by its id, "hub-2") waits on (#66, #86), or null when it's open; `Game` sets it for the map's ladder. */
  needs: (id: string) => string | null = () => null;

  /** The last prompt handed out, handed out again while it hasn't changed. */
  private promptHud: ChallengePrompt | null = null;

  /** No marker until a sim step in free skate finds one. */
  forget(): void {
    this.near = null;
    this.nearHub = null;
  }

  /** The HUD's prompt for the marker or hub the skater's in, checked if `done` says so; null for none. */
  prompt(done: (id: string) => boolean): ChallengePrompt | null {
    const hub = this.nearHub;
    const at = hub ? { id: hub.id, text: hubName(hub.tier) } : this.near;
    return (this.promptHud = challengePrompt(at, done, this.promptHud, this.needs, hub !== null));
  }

  goals(): null {
    return null;
  }

  touch(): void {}

  step(_dt: number, skater: Skater, _events: readonly SkaterEvent[]): void {
    const free = skater.mode === "ground" && !runBusy(skater);
    this.near = free ? spotAt(this.spots, skater.x, skater.z) : null;
    this.nearHub = free ? hubAt(this.hubs, skater.x, skater.z) : null;
  }

  onPress(input: InputFrame): SessionPress | null {
    if (!input.pressed.grind) return null;
    // A locked marker or hub doesn't open: its prompt says what unlocks it.
    if (this.nearHub) return this.needs(this.nearHub.id) === null ? { type: "hub", hub: this.nearHub } : null;
    return this.near && this.needs(this.near.id) === null ? { type: "challenge", spot: this.near } : null;
  }
}

/** Seconds a demo line keeps rolling after its script ends, so the last trick lands on screen. */
export const DEMO_TAIL = 1.2;
/** Buttons that take over from the demo on a desktop (any real play input). */
const PLAY_BUTTONS = ["ollie", "flip", "grab", "grind"] as const;
const NONE = Object.fromEntries(BUTTONS.map((k) => [k, false])) as InputFrame["pressed"];
const IDLE: InputFrame = { moveX: 0, moveY: 0, held: NONE, pressed: NONE, released: NONE, source: "keyboard" };

/** Demo mode: one of `DEMO_LINES` playing through the real sim, as scripted input. */
export class DemoSession implements SessionBase {
  readonly kind = "demo";
  readonly holding = false;
  private player: DemoPlayer;
  /** Sim time since the line's script ended. */
  private tail = 0;

  constructor(
    /** Which of `DEMO_LINES` this is. */
    readonly index: number,
    readonly line: DemoLine,
  ) {
    this.player = new DemoPlayer(line);
  }

  get name(): string {
    return this.line.name;
  }

  /** The script is over and the tail has rolled: time for the next line. */
  get done(): boolean {
    return this.player.done && this.tail >= DEMO_TAIL;
  }

  /** The input for the next sim step (idle once the script is over). */
  next(skater: Skater): InputFrame {
    const f = this.player.next(skater);
    if (f) return f;
    this.tail += DEMO_STEP;
    return IDLE;
  }

  goals(): null {
    return null;
  }

  touch(): void {}

  step(): void {}

  onPress(input: InputFrame): SessionPress | null {
    return PLAY_BUTTONS.some((b) => input.pressed[b]) ? { type: "stopDemo" } : null;
  }
}

/** The 2-minute run (THPS career style). */
export class RunSession implements GoalSession {
  readonly kind = "run";
  readonly run: Run;
  /** `run.version` the goal list was last built at. */
  private goalVersion: number;
  private hudState: RunHud | null = null;

  constructor(
    private host: SessionHost,
    goals: MapGoals,
    pickups: readonly Collectable[],
  ) {
    this.run = new Run(goals, pickups);
    this.goalVersion = this.run.version;
  }

  get holding(): boolean {
    return this.run.phase === "done";
  }

  onPress(): null {
    return null;
  }

  goals(): Goal[] {
    this.goalVersion = this.run.version;
    return this.run.goals.map((g) => ({ ...g }));
  }

  /** The HUD's timer and results state, rebuilt only when what it shows changes. */
  hud(): RunHud {
    const run = this.run;
    const seconds = Math.ceil(run.timeLeft);
    const prev = this.hudState;
    if (prev && prev.seconds === seconds && prev.phase === run.phase && prev.score === run.score && prev.goalsDone === run.goalsDone)
      return prev;
    return (this.hudState = { seconds, phase: run.phase, score: run.score, goalsDone: run.goalsDone });
  }

  touch(skater: Skater): void {
    touchPickups(this.host, this.run, this.run.touch(skater.x, skater.y, skater.z, runBusy(skater)));
  }

  step(dt: number, skater: Skater, events: readonly SkaterEvent[]): void {
    const completed = this.run.step(dt, skater.score, runBusy(skater), events);
    if (completed.length) this.host.play("goal");
    // A goal callout wins over the combo's, which the score already shows.
    for (const g of completed) {
      const paid = this.host.goalDone(g.id);
      this.host.say("goal", `Goal! ${g.text}${paid === null ? `  ${NOT_COUNTED}` : paid ? `  ${statPointsText(paid)}` : ""}`);
    }
    if (this.run.version !== this.goalVersion) this.host.refreshGoals();
  }
}

/** A free-skate challenge (THPS 4 style): one goal on its own clock, started from its marker. */
export class ChallengeSession implements GoalSession {
  readonly kind = "challenge";
  readonly challenge: Challenge;
  private hudState: ChallengeHud | null = null;

  constructor(
    private host: SessionHost,
    spot: ChallengeSpot,
    goals: MapGoals,
    pickups: readonly Collectable[],
  ) {
    this.challenge = new Challenge(spot, goals, pickups);
  }

  get run(): Run {
    return this.challenge.run;
  }

  get holding(): boolean {
    return !this.challenge.live;
  }

  onPress(): null {
    return null;
  }

  goals(): null {
    return null;
  }

  /** The HUD's card and clock, rebuilt only when what it shows changes. */
  hud(): ChallengeHud {
    return (this.hudState = challengeHud(this.challenge, this.hudState)!);
  }

  touch(skater: Skater): void {
    if (!this.challenge.live) return;
    touchPickups(this.host, this.run, this.challenge.touch(skater.x, skater.y, skater.z, runBusy(skater)));
  }

  step(dt: number, skater: Skater, events: readonly SkaterEvent[]): void {
    const ch = this.challenge;
    if (!ch.step(dt, skater.score, runBusy(skater), events, riding(skater))) return;
    this.host.play(ch.phase === "passed" ? "goal" : "fail");
    if (ch.phase === "passed") {
      // The pass card says the challenge; the callout says what it earned.
      const paid = this.host.goalDone(ch.spot.id);
      if (paid === null) this.host.say("goal", NOT_COUNTED);
      else if (paid) this.host.say("goal", statPointsText(paid));
      else this.host.clearMessage();
      this.host.refreshGoals();
    } else this.host.clearMessage(); // the fail card says it
  }
}

/** A goal done with a cheat on (#119). */
export const NOT_COUNTED = "Cheats on: not counted";

/** "+1 stat point", "+2 stat points". */
export function statPointsText(n: number): string {
  return `+${n} stat point${n === 1 ? "" : "s"}`;
}

/** Plays and calls out each pickup the skater just grabbed. */
function touchPickups(host: SessionHost, run: Run, grabbed: readonly Collectable[]): void {
  for (const c of grabbed) {
    host.play("pickup");
    host.say("gap", pickupCallout(run, c));
  }
}

/** What the HUD says when you grab a letter or the tape. */
export function pickupCallout(run: Run, c: Collectable): string {
  if (c.kind === "tape") return "Secret Tape!";
  // Just the letter and the count: the word and which letters are on the clock or the goal list as tiles (#148).
  return `${c.letter}! ${run.lettersGot(c.kind)} of 5 letters`;
}
